<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex shrink-0 items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Payments</h1>
            <div class="flex items-center space-x-2">
                <UInput v-model="search" type="text" placeholder="Search by email..." icon="i-ph-magnifying-glass-duotone" />
                <USelectMenu
                    v-model="filters.status"
                    :options="statusOptions"
                    value-attribute="value"
                    option-attribute="label"
                    placeholder="All statuses"
                    class="w-40"
                    nullable
                />
                <UButton label="Clear" color="gray" @click="clearFilters()" />
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="orders"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-credit-card-duotone', label: 'No payments.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' }, tr: { base: 'cursor-pointer hover:bg-gray-50' } }"
                class="h-full"
                @select="(row) => navigateTo(`/payments/${row.id}`)"
            >
                <template #invoice_number-data="{ row }">
                    <span class="font-mono text-gray-500">#{{ row.invoice_number }}</span>
                </template>
                <template #customer-data="{ row }">
                    <div v-if="row.customer" class="flex items-center space-x-2.5">
                        <Avatar size="sm" :path="row.customer.avatar" :placeholder="[row.customer.first_name, row.customer.last_name].filter(Boolean).join(' ')" />
                        <div class="leading-4">
                            <p class="text-sm font-medium">{{ [row.customer.first_name, row.customer.last_name].filter(Boolean).join(' ') }}</p>
                            <p class="text-xs text-gray-500">{{ row.email }}</p>
                            <PhoneLabel v-if="row.customer.phone" :value="row.customer.phone" class="text-xs text-gray-500" />
                        </div>
                    </div>
                    <span v-else class="text-sm text-gray-500">{{ row.email }}</span>
                </template>
                <template #product-data="{ row }">
                    <span>{{ row.product?.name || '–' }}</span>
                </template>
                <template #members-data="{ row }">
                    <span class="font-mono">{{ row.quantity || '–' }}</span>
                </template>
                <template #amount-data="{ row }">
                    <div v-if="row.total_price != null" class="text-right">
                        <p class="font-mono font-medium">${{ ((row.total_price + row.tax_amount) / 100).toFixed(2) }}</p>
                        <p class="text-xs text-gray-400">incl. ${{ (row.tax_amount / 100).toFixed(2) }} VAT</p>
                    </div>
                    <span v-else class="text-gray-400">–</span>
                </template>
                <template #status-data="{ row }">
                    <UBadge v-if="row.approved_at" color="green" label="Approved" variant="subtle" />
                    <UBadge v-else color="orange" label="Pending" variant="subtle" />
                </template>
                <template #created_at-data="{ row }">
                    <span class="text-sm text-gray-500">{{ dayjs(row.created_at).format('lll') }}</span>
                </template>
                <template #approved_at-data="{ row }">
                    <span v-if="row.approved_at" class="text-sm text-gray-500">{{ dayjs(row.approved_at).format('lll') }}</span>
                    <span v-else class="text-gray-400">–</span>
                </template>
            </UTable>
        </div>

        <div v-if="totalCount > pageSize" class="flex justify-center">
            <UPagination v-model="page" :page-count="pageSize" :total="totalCount" />
        </div>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat.js'
import { watchDebounced } from '@vueuse/core'
import { UserRole } from '~/types/user'

dayjs.extend(localizedFormat)

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Payments' })

const client = useSupabaseClient()

const loading = ref(false)
const orders = ref<any[]>([])
const search = ref('')
const page = ref(1)
const pageSize = 25
const totalCount = ref(0)

const filters = reactive({
    status: null as string | null,
})

const statusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
]

const columns = [
    { key: 'invoice_number', label: 'PO #', class: 'w-px whitespace-nowrap' },
    { key: 'customer', label: 'Customer', class: 'w-full' },
    { key: 'product', label: 'Product' },
    { key: 'members', label: 'Members' },
    { key: 'amount', label: 'Amount' },
    { key: 'status', label: 'Status' },
    { key: 'created_at', label: 'Created' },
    { key: 'approved_at', label: 'Approved' },
]

onMounted(() => fetchOrders())
watchDebounced(search, () => { page.value = 1; fetchOrders() }, { debounce: 600 })
watch([() => filters.status, page], () => fetchOrders())

function clearFilters() {
    search.value = ''
    filters.status = null
    page.value = 1
    fetchOrders()
}

async function fetchOrders() {
    loading.value = true

    const from = (page.value - 1) * pageSize
    const to = from + pageSize - 1

    let query = client
        .from('purchase_orders')
        .select(`
            id, invoice_number, email, quantity, unit_price, total_price, tax_amount, approved_at, created_at,
            product:products (id, name),
            customer:customers (id, first_name, last_name, avatar, phone)
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to)

    if (search.value) {
        query = query.ilike('email', `%${search.value}%`)
    }

    if (filters.status === 'pending') {
        query = query.is('approved_at', null)
    } else if (filters.status === 'approved') {
        query = query.not('approved_at', 'is', null)
    }

    const { data, count } = await query

    if (data) orders.value = data as any
    if (count != null) totalCount.value = count

    loading.value = false
}
</script>
